import React, { useState } from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  Font,
  PDFDownloadLink,
  pdf,
  Link,
  Svg,
  Path,
} from "@react-pdf/renderer";
import { Button, message } from "antd";
import { prepareItineraryImages } from "./imageCache";
import { DownloadOutlined } from "@ant-design/icons";
import { BRAND, styles } from "./TripItineraryPDF.styles";
import {
  ROUTE_SEPARATOR,
  PdfIcon,
  PromotionIcon,
  GroupBadgeIcon,
  CalendarBadgeIcon,
  StatusBadgeIcon,
} from "./utils";
// --------------------------------------------------
// FONT CONFIGURATION
// Files must exist inside public/fonts/
// --------------------------------------------------

Font.register({
  family: "NotoSans",
  fonts: [
    {
      src: `${process.env.PUBLIC_URL}/fonts/NotoSans-Regular.ttf`,
      fontWeight: 400,
    },
    {
      src: `${process.env.PUBLIC_URL}/fonts/NotoSans-Bold.ttf`,
      fontWeight: 700,
    },
  ],
});
// --------------------------------------------------
// PDF SVG ICONS
// --------------------------------------------------
// const ROUTE_SEPARATOR = "  >  ";
// --------------------------------------------------
// BRAND CONFIGURATION
// --------------------------------------------------

// --------------------------------------------------
// STYLES
// --------------------------------------------------

// --------------------------------------------------
// HELPER FUNCTIONS
// --------------------------------------------------

const formatPrice = (price, currency = "INR") => {
  if (price === undefined || price === null) return "";

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  } catch {
    return `${currency} ${price}`;
  }
};

// --------------------------------------------------
// COVER ICONS
// Built with React PDF native components
// --------------------------------------------------

// --------------------------------------------------
// COMMON SECTION
// --------------------------------------------------

const Section = ({ title, children }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

// --------------------------------------------------
// BULLET LIST
// --------------------------------------------------

const BulletList = ({ items = [] }) => (
  <View>
    {items.map((item, index) => (
      <View key={index} style={styles.listItem}>
        <Text style={styles.listBullet}>•</Text>
        <Text style={styles.listText}>{String(item)}</Text>
      </View>
    ))}
  </View>
);

// --------------------------------------------------
// MEAL COMPONENT
// --------------------------------------------------

const MealStatus = ({ meals }) => {
  if (!meals || typeof meals !== "object") return null;

  const mealLabels = {
    breakfast: "Breakfast",
    lunch: "Lunch",
    dinner: "Dinner",
  };

  return (
    <View style={styles.mealRow}>
      {Object.entries(meals).map(([key, value]) => {
        const included =
          typeof value === "object" && value !== null
            ? value.included === true
            : value === true;

        const label = mealLabels[key] || key;

        return (
          <View
            key={key}
            style={[
              included ? styles.mealIncluded : styles.mealExcluded,
              {
                width: 95,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
              },
            ]}
          >
            <Text
              style={included ? { color: BRAND.green } : { color: "#66717E" }}
            >
              {label}
            </Text>

            <PdfIcon
              type={included ? "check" : "close"}
              color={included ? BRAND.green : BRAND.red}
              size={13}
            />
          </View>
        );
      })}
    </View>
  );
};
// --------------------------------------------------
// DAY COMPONENT
// --------------------------------------------------

const TripDay = ({ step, index }) => (
  <View style={styles.dayCard} wrap={false}>
    <Text style={styles.dayNumber}>
      {(step.day || `DAY ${index + 1}`).toUpperCase()}
    </Text>

    <Text style={styles.dayHeading}>{step.title || "Trip Activities"}</Text>

    {step.subtitle ? (
      <Text style={styles.daySubtitle}>{step.subtitle}</Text>
    ) : null}

    {step.image ? <Image src={step.image} style={styles.dayImage} /> : null}

    {(step.details || []).map((detail, i) => (
      <View key={i} style={styles.detailRow}>
        <Text style={styles.bullet}>•</Text>
        <Text style={styles.detailText}>{String(detail)}</Text>
      </View>
    ))}

    <MealStatus meals={step.meals} />

    {step.overnight ? (
      <View
        style={{
          marginTop: 9,
          padding: 9,
          backgroundColor: BRAND.lightPurple,
          borderRadius: 5,
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <PdfIcon type="moon" color={BRAND.purple} size={17} />

        <Text
          style={{
            marginLeft: 8,
            fontSize: 9,
            color: BRAND.purple,
          }}
        >
          OVERNIGHT STAY: {step.overnight}
        </Text>
      </View>
    ) : null}
  </View>
);

// --------------------------------------------------
// TRANSFER COMPONENT
// --------------------------------------------------

const TripTransfer = ({ transfer }) => (
  <View style={styles.transferCard} wrap={false}>
    <Text style={styles.transferRoute}>
      {transfer.from || "Starting point"} {"  >  "}{" "}
      {transfer.to.replace(/\s*→\s*/g, ROUTE_SEPARATOR) || "Destination"}
    </Text>

    <Text style={styles.transferInfo}>
      Transport: {transfer.type || "Not specified"}
      {transfer.duration ? `  |  Duration: ${transfer.duration}` : ""}
    </Text>

    <Text
      style={[
        styles.transferInfo,
        transfer.included ? styles.includedText : styles.excludedText,
      ]}
    >
      {transfer.included ? "Included" : "Not included"}
    </Text>

    {transfer.note ? (
      <Text style={styles.transferInfo}>Note: {transfer.note}</Text>
    ) : null}
  </View>
);
// --------------------------------------------------
// STAY OPTIONS & ADD-ONS
// --------------------------------------------------

const getOptionPrice = (item, currency = "INR") => {
  if (item.price == null || item.price_type === "ask_price") {
    return "Price on request";
  }

  const suffix =
    {
      per_person: "/ person",
      per_couple: "/ couple",
      per_group: "/ group",
      fixed: "",
    }[item.price_type] || "";

  return `${formatPrice(item.price, currency)} ${suffix}`.trim();
};

const OptionCard = ({ item, currency }) => (
  <View style={styles.optionCard} wrap={false}>
    <Text style={styles.optionName}>{item.name}</Text>
    <Text style={styles.optionPrice}>{getOptionPrice(item, currency)}</Text>
  </View>
);

const StayOptionsPDF = ({ options, currency }) => {
  const activeOptions = Array.isArray(options)
    ? options.filter((item) => item.enabled === true)
    : [];

  if (!activeOptions.length) return null;

  return (
    <View style={styles.optionSection}>
      <Section title="STAY OPTIONS" />

      <View style={styles.optionGrid}>
        {activeOptions.map((item, index) => (
          <OptionCard
            key={`${item.name}-${index}`}
            item={item}
            currency={currency}
          />
        ))}
      </View>
    </View>
  );
};

const AddonsPDF = ({ options, currency }) => {
  const activeOptions = Array.isArray(options)
    ? options.filter((item) => item.enabled === true)
    : [];

  if (!activeOptions.length) return null;

  const categories = [
    { key: "activity", label: "Activities" },
    { key: "stay", label: "Stay Upgrades" },
    { key: "transport", label: "Transport" },
  ];

  return (
    <View style={styles.optionSection}>
      <Section title="OPTIONAL ADD-ONS" />

      {categories.map((category) => {
        const items = activeOptions.filter(
          (item) => item.category?.toLowerCase() === category.key,
        );

        if (!items.length) return null;

        return (
          <View key={category.key} style={{ marginBottom: 7 }}>
            <Text style={styles.optionCategory}>{category.label}</Text>

            <View style={styles.optionGrid}>
              {items.map((item, index) => (
                <OptionCard
                  key={`${item.name}-${index}`}
                  item={item}
                  currency={currency}
                />
              ))}
            </View>
          </View>
        );
      })}
    </View>
  );
};
// --------------------------------------------------
// MAIN PDF DOCUMENT
// --------------------------------------------------

const TripItineraryPDF = ({ data }) => {
  if (!data) return null;

  const requirements = data.pre_requerments || data.pre_requirements || [];

  const payment = data.payment_options || {};

  const paymentItems = [
    `Full payment: ${payment.full_payment ? "Available" : "Not available"}`,

    `EMI: ${payment.emi_available ? "Available" : "Not available"}`,

    ...(payment.emi_tenure?.length
      ? [`EMI tenure: ${payment.emi_tenure.join(", ")}`]
      : []),

    ...(payment.payment_methods?.length
      ? [`Payment methods: ${payment.payment_methods.join(", ")}`]
      : []),

    ...(payment.emi_note ? [payment.emi_note] : []),
  ];

  const coverImage =
    data.cover_image ||
    data.photos?.[0] ||
    data.itinerary?.find((day) => day.image)?.image;

  const logo = data.logo || BRAND.logo;

  return (
    <Document
      title={`${data.title || "Trip"} - DIVE HRZN Itinerary`}
      author={BRAND.name}
      subject="Official trip itinerary"
    >
      <Page size={[595.28, 10000]} style={styles.page} wrap={false}>
        {/* WATERMARK */}
        <View style={styles.watermark}>
          <Text>DIVE HRZN</Text>
        </View>

        {/* PREMIUM COVER */}
        <View style={styles.hero}>
          {coverImage ? (
            <Image src={coverImage} style={styles.heroImage} />
          ) : null}

          <View style={styles.heroOverlay} />

          <View style={styles.heroContent}>
            {/* LOGO + BRAND NAME */}
            <View style={styles.heroBrandRow}>
              {logo ? <Image src={logo} style={styles.heroLogo} /> : null}

              <View style={styles.heroBrandDetails}>
                <Text style={styles.heroBrand}>{BRAND.name}</Text>
                <Text style={styles.heroTagline}>{BRAND.tagline}</Text>
              </View>
            </View>

            {/* BRAND TAGLINE */}

            {/* TRIP TITLE */}
            <Text style={styles.heroTitle}>
              {data.title || "Adventure Trip"}
            </Text>

            <Text style={styles.heroLocation}>
              {data.place || ""}
              {data.dates ? `  |  ${data.dates}` : ""}
            </Text>

            {data.SKU ? <Text style={styles.heroSku}>{data.SKU}</Text> : null}

            {/* SLANTED PRICE BANNER */}
            {data.price !== undefined ? (
              <View style={styles.heroPriceBox}>
                <Svg
                  width={215}
                  height={55}
                  viewBox="0 0 215 55"
                  style={styles.heroPriceShape}
                >
                  <Path
                    d="M18 0 H201 Q215 0 211 13 L197 45 Q194 55 183 55 H12 Q0 55 4 43 L17 8 Q18 0 18 0 Z"
                    fill={BRAND.yellow}
                  />
                </Svg>

                <View style={styles.heroPriceContent}>
                  <Text style={styles.heroPrice}>
                    {formatPrice(data.price, data.currency)}
                  </Text>

                  <Text style={styles.heroPriceLabel}> / person</Text>
                </View>
              </View>
            ) : null}

            {/* COVER BADGES */}
            <View style={styles.heroStats}>
              {data.group_size ? (
                <View style={styles.heroStat}>
                  <GroupBadgeIcon />
                  <Text style={styles.heroStatText}>
                    Group size: {data.group_size}
                  </Text>
                </View>
              ) : null}

              {data.dates ? (
                <View style={styles.heroStat}>
                  <CalendarBadgeIcon />
                  <Text style={styles.heroStatText}>{data.dates}</Text>
                </View>
              ) : null}

              {data.status ? (
                <View style={styles.heroStat}>
                  <StatusBadgeIcon />
                  <Text style={styles.heroStatText}>Status: {data.status}</Text>
                </View>
              ) : null}
            </View>
            {/* HERO CONTACT BUTTONS */}
            {/* HERO CONTACT BUTTONS */}
            <View style={styles.heroActions}>
              <Link
                src="https://wa.me/917092925555"
                style={styles.heroActionLink}
              >
                <View style={styles.heroActionButton}>
                  <Text style={styles.heroActionText}>CHAT WITH US</Text>
                </View>
              </Link>

              <Link
                src="https://chat.whatsapp.com/H1aKcwz26Eg9Dw4SWUXSC7"
                style={styles.heroActionLink}
              >
                <View style={styles.heroActionButton}>
                  <Text style={styles.heroActionText}>JOIN COMMUNITY</Text>
                </View>
              </Link>

              <Link
                src="https://www.divehrzn.com"
                style={styles.heroActionLink}
              >
                <View style={styles.heroActionButton}>
                  <Text style={styles.heroActionText}>WEBSITE</Text>
                </View>
              </Link>
            </View>
          </View>
        </View>
        {/* SPECIAL OFFERS */}
        {/* SPECIAL OFFERS */}
        {data.promotions?.some((item) => item.enabled === true) ? (
          <View style={styles.promotionContainer}>
            {data.promotions
              .filter((item) => item.enabled === true)
              .map((item, index, offers) => (
                <React.Fragment key={index}>
                  <View style={styles.promotionItem}>
                    <PromotionIcon title={item.title} />

                    <Text style={styles.promotionTitle}>{item.title}</Text>

                    <Text style={styles.promotionDescription}>
                      {item.description}
                    </Text>
                  </View>

                  {index < offers.length - 1 ? (
                    <View style={styles.promotionDivider} />
                  ) : null}
                </React.Fragment>
              ))}
          </View>
        ) : null}

        {/* ROUTE */}
        {data.route ? (
          <Section title="TRIP ROUTE">
            <View style={styles.routeBox}>
              <Text style={styles.routeText}>
                {data.route.replace(/\s*→\s*/g, ROUTE_SEPARATOR)}
              </Text>
            </View>
          </Section>
        ) : null}

        {/* DAY-WISE ITINERARY */}
        {data.itinerary?.length > 0 ? (
          <Section title="DAY-WISE ITINERARY">
            {data.itinerary.map((step, index) => (
              <TripDay key={step.day || index} step={step} index={index} />
            ))}
          </Section>
        ) : null}

        {/* TRANSFERS */}
        {data.transfers?.length > 0 ? (
          <Section title="TRANSPORT & TRANSFERS">
            {data.transfers.map((transfer, index) => (
              <TripTransfer
                key={`${transfer.afterDay}-${transfer.beforeDay}-${index}`}
                transfer={transfer}
              />
            ))}
          </Section>
        ) : null}

        {/* INCLUSIONS */}
        {data.included?.length > 0 ? (
          <Section title="PACKAGE INCLUDES">
            <BulletList items={data.included} />
          </Section>
        ) : null}

        {/* EXCLUSIONS */}
        {data.excluded?.length > 0 ? (
          <Section title="PACKAGE EXCLUDES">
            <BulletList items={data.excluded} />
          </Section>
        ) : null}

        {/* REQUIREMENTS */}
        {requirements.length > 0 ? (
          <Section title="PRE-TRIP REQUIREMENTS">
            <BulletList items={requirements} />
          </Section>
        ) : null}
        {/* STAY OPTIONS */}
        <StayOptionsPDF
          options={data.stay_options}
          currency={data.currency || "INR"}
        />

        {/* OPTIONAL ADD-ONS */}
        <AddonsPDF options={data.addons} currency={data.currency || "INR"} />
        {/* DESCRIPTION */}
        {data.description ? (
          <Section title="ABOUT THIS TRIP">
            <Text style={styles.paragraph}>{data.description}</Text>
          </Section>
        ) : null}

        {/* PAYMENT OPTIONS */}
        {paymentItems.length > 0 ? (
          <Section title="PAYMENT OPTIONS">
            <View style={styles.paymentBox}>
              <BulletList items={paymentItems} />
            </View>
          </Section>
        ) : null}

        {/* CANCELLATION */}
        {data.cancellation_policy?.length > 0 ? (
          <Section title="CANCELLATION POLICY">
            <BulletList items={data.cancellation_policy} />
          </Section>
        ) : null}

        {/* TERMS */}
        {data.terms_and_conditions?.length > 0 ? (
          <Section title="TERMS & CONDITIONS">
            <BulletList items={data.terms_and_conditions} />
          </Section>
        ) : null}

        {/* PHOTO GALLERY */}
        {data.photos?.length > 0 ? (
          <Section title="TRIP GALLERY">
            <View style={styles.gallery}>
              {data.photos.map((photo, index) => (
                <Image key={index} src={photo} style={styles.galleryImage} />
              ))}
            </View>
          </Section>
        ) : null}

        {/* TAGS */}
        {data.tags?.length > 0 ? (
          <Section title="TRIP TAGS">
            <Text style={styles.paragraph}>{data.tags.join("  •  ")}</Text>
          </Section>
        ) : null}

        <Section title="CONTACT US">
          <Link src="https://wa.me/917092925555" style={styles.contactLink}>
            Chat with Our Team on WhatsApp
          </Link>

          <Link
            src="https://chat.whatsapp.com/H1aKcwz26Eg9Dw4SWUXSC7"
            style={styles.contactLink}
          >
            Join WhatsApp Community
          </Link>

          <Link src="https://www.divehrzn.com" style={styles.contactLink}>
            Visit DIVE HRZN Website
          </Link>
        </Section>
        {/* FOOTER */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © {new Date().getFullYear()} DIVE HRZN. All rights reserved.
          </Text>

          <Text style={styles.footerText}>OFFICIAL TRIP ITINERARY</Text>
        </View>
      </Page>
    </Document>
  );
};

// --------------------------------------------------
// DOWNLOAD BUTTON — GENERATE ONLY ON CLICK
// --------------------------------------------------

export const DownloadItineraryButton = ({ data, type }) => {
  const [loading, setLoading] = useState(false);

  if (!data?.itinerary?.length) return null;

  const fileName = `${(data.title || "Trip-Itinerary")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-|-$/g, "")}-DIVE-HRZN.pdf`;

  const handleDownload = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const blob = await pdf(<TripItineraryPDF data={data} />).toBlob();

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = fileName;
      anchor.style.display = "none";

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("PDF download failed:", error);
      message.error("Unable to download itinerary. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="default"
      onClick={handleDownload}
      loading={loading}
      disabled={loading}
      style={{ margin: 8 }}
    >
      {type === "card" ? (
        <DownloadOutlined style={{ fontSize: "large" }} />
      ) : (
        "Download Itinerary"
      )}
    </Button>
  );
};

export default TripItineraryPDF;
